/**
 * Client-side paging for lists that are fetched in full but should only render a
 * slice at a time.
 *
 * A workspace folder is fetched whole (paeapi has no paging, and shipped app builds
 * depend on that), which is fine for the usual handful of files. The rare folder with
 * thousands of them froze the UI because every row was rendered at once, so web and
 * NativeScript both show one page of this size and a Prev/Next control.
 */

/** Files shown per page in a workspace folder, on every surface. */
export const WORKSPACE_FILE_PAGE_SIZE = 100;

export interface ListPage<T> {
  /** The rows on this page. */
  items: T[];
  /** Zero-based page index, clamped into range. */
  page: number;
  pageCount: number;
  /** 1-based position of the first row on this page; 0 when the list is empty. */
  start: number;
  /** 1-based position of the last row on this page; 0 when the list is empty. */
  end: number;
  total: number;
  hasPrev: boolean;
  hasNext: boolean;
}

/**
 * Returns one page of `items`. An out-of-range `page` is clamped, so a refresh that
 * shrinks the list (a delete, a move) lands on the last page that still exists rather
 * than on an empty one.
 */
export function getListPage<T>(items: T[] | null | undefined, page: number, pageSize: number = WORKSPACE_FILE_PAGE_SIZE): ListPage<T> {
  const list = items || [];
  const total = list.length;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const current = Math.min(Math.max(0, Math.floor(page) || 0), pageCount - 1);
  const from = current * pageSize;
  const pageItems = list.slice(from, from + pageSize);
  return {
    items: pageItems,
    page: current,
    pageCount,
    start: pageItems.length ? from + 1 : 0,
    end: from + pageItems.length,
    total,
    hasPrev: current > 0,
    hasNext: current < pageCount - 1,
  };
}
